<script setup lang="ts">
import { computed, ref } from 'vue'
import { capitalizeFirstLetter, formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import CheckCircleThin from '@/components/icons/CheckCircleThin.vue'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import api from '@/services/api'
import SpinnerIcon from '@/components/icons/SpinnerIcon.vue'
import { useAnalytics } from '@/composables/analytics'
import type { PlayerStats } from '@/types/shared/player'
import type { PlayerFortuneWheelState } from '@/types/shared/fortune-wheel'
import { MILESTONE_TYPES, type MilestoneReward } from '@/types/milestones'

const props = defineProps<{
	reward: MilestoneReward
	type: MILESTONE_TYPES
	claimed: boolean
	claimable: boolean
	goldenCoinValue: number
}>()

const emit = defineEmits<{
	(e: 'claim', payload: { playerStats: PlayerStats, fortuneWheelState: PlayerFortuneWheelState }): void
}>()

enum CardState {
	CLAIMED = 'claimed',
	CLAIMABLE = 'claimable',
	UNCLAIMABLE = 'unclaimable',
}

const { triggerEvent } = useAnalytics()

const cardState = computed(() => {
  if (props.claimed) return CardState.CLAIMED
  if (props.claimable) return CardState.CLAIMABLE
  return CardState.UNCLAIMABLE
})
const isClaimed = computed(() => cardState.value === CardState.CLAIMED)
const isClaimable = computed(() => cardState.value === CardState.CLAIMABLE)
const isUnclaimable = computed(() => cardState.value === CardState.UNCLAIMABLE)
const goldenCoinReward = computed(() => props.reward.reward.goldenCoins * props.goldenCoinValue)
const spinsReward = computed(() => props.reward.reward.spins)
const ticketRewards = computed(() => props.reward.reward.tickets)
const badgeReward = computed(() => !!props.reward.reward.badgeId)
const isClaiming = ref(false)
const dollarAmount = computed(() => props.reward.minLimit / 100)

const rewardsCount = computed(() => Number(!!goldenCoinReward.value) + Number(!!spinsReward.value) + Number(badgeReward.value) + Number(!!ticketRewards.value))

async function claim() {
  if (!isClaimable.value || isClaiming.value) return
  isClaiming.value = true
  let result
  try {
    if (props.type === MILESTONE_TYPES.PURCHASE) {
      result = await api.claimPurchaseReward(props.reward.minLimit)
    } else if (props.type === MILESTONE_TYPES.TRANSACTION) {
      result = await api.claimTonTxCountReward(props.reward.minLimit)
    } else if (props.type === MILESTONE_TYPES.PATHWAYS) {
      result = await api.claimPathwayCountReward(props.reward.minLimit)
    } else {
      throw new Error('Invalid milestone type')
    }
    if (!result.successful) {
      throw new Error(`Could not claim ${props.type} reward`)
    }
    emit('claim', { playerStats: result.playerStats, fortuneWheelState: result.fortuneWheelState })
    triggerEvent({
      event: `${props.type}_amount_${props.type === MILESTONE_TYPES.PURCHASE ? dollarAmount.value : props.reward.minLimit}`,
      metadata: { minAmountCents: props.reward.minLimit },
    })
  } catch (e) {
    await api.reportError({
      code: `couldNotClaim${capitalizeFirstLetter(props.type)}Reward`,
      description: `Could not claim ${props.type} reward`,
      metadata: { minAmountCents: props.reward.minLimit },
    })
  } finally {
    isClaiming.value = false
  }
}
</script>

<template>
  <div
    class="rounded-lg relative"
    :class="{'opacity-[0.68] pointer-events-none': isUnclaimable, 'shadow-claimable scale-up z-[10]': isClaimable}"
    @click="claim"
  >
    <CheckCircleThin
      v-if="isClaimed"
      class="absolute w-6 h-6 min-w-[24px] -top-2 -right-2 text-teal-primary scale-in z-[1]"
    />
    <div class="rounded-lg pt-2.5 w-full h-full flex flex-col gap-y-1.5 overflow-hidden bg-dark-top-layer relative">
      <i
        v-if="isClaimable"
        class="shine w-[25%]"
      />
      <div
        class="flex-1 flex flex-col items-center justify-center"
        :class="rewardsCount >= 4 ? '-space-y-2' : (rewardsCount >= 3 ? '-space-y-1' : 'gap-y-1')"
      >
        <div
          class="flex items-center"
          v-if="goldenCoinReward"
        >
          <img
            :src="CoinIcon"
            class="h-4 w-auto min-w-[10px] mr-1"
          >
          <span
            class="text-base font-semibold -tracking-[0.32px]"
          >+{{
            goldenCoinReward <= 10_000 ? formatUnshortenedNumber(goldenCoinReward) : formatNumberFullTruncate(goldenCoinReward)
          }}</span>
        </div>
        <div
          v-if="spinsReward"
          class="flex items-center"
        >
          <img
            class="object-cover size-4 min-w-[16px] mr-1.5"
            src="@/assets/images/fortune-wheel.webp"
          >
          <span class="text-base font-semibold -tracking-[0.32px]">+{{ formatNumberFullTruncate(spinsReward) }}</span>
        </div>
        <div
          v-if="ticketRewards"
          class="flex items-center"
        >
          <img
            class="object-cover size-4 min-w-[16px] mr-1.5"
            src="@/assets/images/emojis/emoji-ticket.png"
          >
          <span class="text-base font-semibold -tracking-[0.32px]">+{{ formatNumberFullTruncate(ticketRewards) }}</span>
        </div>
        <div
          v-if="badgeReward"
          class="flex items-center"
        >
          <img
            class="object-cover size-4 min-w-[16px] mr-1"
            src="@/assets/images/badges/badge.png"
          >
          <span class="text-base font-semibold -tracking-[0.32px]">+1</span>
        </div>
      </div>
      <div
        class="h-6 flex items-center justify-center text-xs leading-6 font-bold"
        :class="isClaimable ? 'bg-teal-primary text-tropee-black' : 'bg-dark-top-most-layer text-white/[.87]'"
      >
        <span v-if="isClaimable">
          <SpinnerIcon
            v-if="isClaiming"
            class="loading-spinner h-4 w-4 min-w-[16px] mx-auto"
          /> <span v-else>CLAIM</span></span>
        <span v-else-if="type === MILESTONE_TYPES.PURCHASE">${{
          dollarAmount >= 100_000 ? formatNumberFullTruncate(dollarAmount) : formatUnshortenedNumber(dollarAmount)
        }}</span>
        <span v-else>{{ reward.minLimit }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shadow-claimable {
	box-shadow: 0px 0px 24px 0px #14F1FF;
}

.scale-up {
	animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
	from {
		transform: scale(1);
	}
	to {
		transform: scale(1.03);
	}
}
</style>